<template>
  <v-form
    ref="formRef"
    class="electric-drive-form method-form"
    @submit.prevent=""
  >
    <!-- Basisdaten -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-counter</v-icon>
        <span>Basisdaten</span>
      </div>

      <!-- Anzahl -->
      <v-text-field
        v-model.number="form.amount"
        type="number"
        :error-messages="validationErrors?.amount"
        label="Anzahl"
        :hint="hint.amount"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.amount" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.amount }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.amount"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Anzahl übernehmen"
            @click="copyFromReference('amount')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Motordaten -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-engine-outline</v-icon>
        <span>Motordaten</span>
      </div>

      <!-- Nennleistung -->
      <v-autocomplete
        v-model="form.ratedPower"
        :error-messages="validationErrors?.rated_power"
        label="Nennleistung"
        :items="ratedPowers"
        item-title="text"
        item-value="value"
        suffix="kW"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.rated_power" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.rated_power }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.rated_power"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Nennleistung übernehmen"
            @click="copyFromReference('rated_power')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Bauart -->
      <v-autocomplete
        v-model="form.motorType"
        :error-messages="validationErrors?.motor_type"
        label="Bauart"
        :items="motorTypes"
        item-title="text"
        item-value="value"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.motor_type" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.motor_type }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.motor_type"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Bauart übernehmen"
            @click="copyFromReference('motor_type')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- VSD / FU -->
      <v-checkbox
        v-model="form.vsdFu"
        :error-messages="validationErrors?.vsd_fu"
        label="Frequenzumrichter (VSD / FU)"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.vsd_fu" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.vsd_fu }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.vsd_fu"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Frequenzumrichter übernehmen"
            @click="copyFromReference('vsd_fu')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-checkbox>

      <!-- Polzahl -->
      <v-autocomplete
        v-model="form.poles"
        :error-messages="validationErrors?.poles"
        label="Polzahl"
        :items="poleList"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.poles" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.poles }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.poles"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Polzahl übernehmen"
            @click="copyFromReference('poles')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Effizienzklasse -->
      <v-autocomplete
        v-model="form.efficiencyClass"
        :error-messages="validationErrors?.efficiency_class"
        label="Effizienzklasse"
        :items="efficiencyClasses"
        item-title="text"
        item-value="value"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.efficiency_class" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.efficiency_class }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.efficiency_class"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Effizienzklasse übernehmen"
            @click="copyFromReference('efficiency_class')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Mechanische Leistung -->
      <v-text-field
        v-model.number="form.mechanicalDemand"
        type="number"
        :error-messages="validationErrors?.mechanical_demand"
        label="Mechanische Leistung"
        suffix="kW"
        :hint="hint.mechanical_demand"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.mechanical_demand" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.mechanical_demand }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.mechanical_demand"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für mechanische Leistung übernehmen"
            @click="copyFromReference('mechanical_demand')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Lastverteilung -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-gauge</v-icon>
        <span>Lastverteilung</span>
      </div>

      <!-- Volllast -->
      <v-text-field
        v-model.number="form.fullLoad"
        type="number"
        :error-messages="validationErrors?.full_load"
        label="Anteil Volllast"
        suffix="%"
        :hint="hint.full_load"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.full_load" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.full_load }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.full_load"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Volllastanteil übernehmen"
            @click="copyFromReference('full_load')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- 75% Teillast -->
      <v-text-field
        v-if="
          (form.fullLoad !== null && form.fullLoad < 100) ||
          form.partLoad75
        "
        v-model.number="form.partLoad75"
        type="number"
        :error-messages="validationErrors?.part_load_75"
        label="Anteil 75% Teillast"
        suffix="%"
        :hint="hint.part_load_75"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.part_load_75" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.part_load_75 }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.part_load_75"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für 75 Prozent Teillast übernehmen"
            @click="copyFromReference('part_load_75')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- 50% Teillast -->
      <v-text-field
        v-if="
          (form.fullLoad !== null && form.fullLoad < 100) ||
          form.partLoad50
        "
        v-model.number="form.partLoad50"
        type="number"
        :error-messages="validationErrors?.part_load_50"
        label="Anteil 50% Teillast"
        suffix="%"
        :hint="hint.part_load_50"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.part_load_50" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.part_load_50 }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.part_load_50"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für 50 Prozent Teillast übernehmen"
            @click="copyFromReference('part_load_50')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- 25% Teillast -->
      <v-text-field
        v-if="
          (form.fullLoad !== null && form.fullLoad < 100) ||
          form.partLoad25
        "
        v-model.number="form.partLoad25"
        type="number"
        :error-messages="validationErrors?.part_load_25"
        label="Anteil 25% Teillast"
        suffix="%"
        :hint="hint.part_load_25"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.part_load_25" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.part_load_25 }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.part_load_25"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für 25 Prozent Teillast übernehmen"
            @click="copyFromReference('part_load_25')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Betrieb -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-clock-outline</v-icon>
        <span>Betrieb</span>
      </div>

      <!-- Betriebsstunden -->
      <v-text-field
        v-model.number="form.uptime"
        type="number"
        :error-messages="validationErrors?.uptime"
        label="Jährliche Betriebsstunden"
        suffix="h/a"
        :hint="hint.uptime"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.uptime"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Betriebsstunden übernehmen"
            @click="copyFromReference('uptime')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>
  </v-form>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import api from '@/plugins/axios'
import { djangoChoicesToVuetify } from '@/utils'
import { useBaseForm } from '@/composables/useBaseForm'

/* Props */
const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  reference: { type: Boolean, default: false },
  planned: { type: Boolean, default: false },
  otherData: { type: Object, default: () => ({}) },
  sharedData: { type: Object, default: () => ({}) },
  validationErrors: { type: Object, default: () => ({}) },
  methodValue: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue'])

/* Pinia store */

/* Fields mapping */
const fields = {
  actionId: 'action_id',
  amount: 'amount',
  ratedPower: 'rated_power',
  motorType: 'motor_type',
  vsdFu: 'vsd_fu',
  poles: 'poles',
  efficiencyClass: 'efficiency_class',
  mechanicalDemand: 'mechanical_demand',
  fullLoad: 'full_load',
  partLoad75: 'part_load_75',
  partLoad50: 'part_load_50',
  partLoad25: 'part_load_25',
  uptime: 'uptime',
}

/* useBaseForm */
const {
  form,
  hint,
  copyFromReference,
  referenceData,
  fetchHints,
} = useBaseForm({
  value: computed(() => props.modelValue),
  fields,
  defaults: { partLoad75: 0, partLoad50: 0, partLoad25: 0 },
  reference: computed(() => props.reference),
  planned: computed(() => props.planned),
  shared: computed(() => false),
  otherData: computed(() => props.otherData),
  sharedData: computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit: (e, p) => emit(e, p),
})

/* Local choices */
const ratedPowers = ref([])
const motorTypes = ref([])
const poleList = ref([2, 4, 6])
const efficiencyClasses = ref([])

/* Fetchers (updated endpoints) */
async function fetchRatedPowers() {
  try {
    const { data } = await api.get('/methods/electricdrive2/rated-powers/')
    ratedPowers.value = djangoChoicesToVuetify(data)
  } catch {}
}
async function fetchMotorTypes() {
  try {
    const { data } = await api.get('/methods/electricdrive2/motor-types/')
    motorTypes.value = djangoChoicesToVuetify(data)
  } catch {}
}
async function fetchEfficiencyClasses() {
  try {
    const { data } = await api.get('/methods/electricdrive2/efficiency-classes/')
    efficiencyClasses.value = djangoChoicesToVuetify(data)
  } catch {}
}

/* Validate API for parent */
const formRef = ref(null)
function validateForm() {
  return formRef.value?.validate?.()
}


defineExpose({
  validateForm,
  fields: Object.values(fields),
})

/* Lifecycle */
onMounted(async () => {
  await Promise.all([
    fetchRatedPowers(),
    fetchMotorTypes(),
    fetchEfficiencyClasses(),
  ])
  await fetchHints()
})
</script>


<style scoped lang="scss">
.electric-drive-form {
  width: 100%;
}

/* Input fields */
.electric-drive-form :deep(.v-field) {
  border-radius: 18px;
}

.electric-drive-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Visual sections */
.method-section {
  margin-bottom: 1.25rem;
  padding: 1rem;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

.method-section:last-child {
  margin-bottom: 0;
}

/* Section headings */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;

  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;

  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;

  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);

  font-size: 1rem;
  font-weight: 700;
}

/* Checkbox */
.electric-drive-form :deep(.v-checkbox) {
  padding: 0.35rem 0.65rem;
  border-radius: 18px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

/* Nested teillast values */
.electric-drive-form :deep(.ml-8) {
  margin-left: 1.5rem !important;
  padding-left: 1rem;
  border-left: 2px solid rgba(var(--v-theme-primary), 0.12);
}

/* Help and reference-copy actions */
.electric-drive-form :deep(.v-input__append),
.electric-drive-form :deep(.v-field__append-inner) {
  align-items: center;
}

.electric-drive-form :deep(.v-input__append .v-icon),
.electric-drive-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.electric-drive-form :deep(.v-input__append .v-btn),
.electric-drive-form :deep(.v-field__append-inner .v-btn) {
  border-radius: 999px;
  color: rgb(var(--v-theme-primary));
}

.electric-drive-form :deep(.v-input__append .v-btn:hover),
.electric-drive-form :deep(.v-field__append-inner .v-btn:hover) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Disabled states */
.electric-drive-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

/* Responsive */
@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }

  .electric-drive-form :deep(.ml-8) {
    margin-left: 0.75rem !important;
    padding-left: 0.75rem;
  }
}
</style>
``
